import { useAuth0 } from '@auth0/auth0-react'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { isEqual } from 'lodash'
import { useRouter, useSearchParams } from 'next/navigation'
import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import config from '~/configs/env'
import { setAccessTokenSilently, setLogout } from '~/data/api/client/security'
import { useUser } from '~/data/api/hooks/users'
import { useBootMark } from '~/debug/bootProfiler'
import useLogger from '~/debug/logger'
import { tagDesktopTelemetry } from '~/features/desktop/telemetry'
import useFirstRender from '~/hooks/useFirstRender'
import { writeCachedPreferredLanguage } from '~/i18n/i18nCache'
import { clearTimezoneSnooze } from '~/layouts/components/banners/TimezoneBanner'
import { clearCloakedOrgPath } from '~/layouts/components/orgHierarchy/cloakSessionStorage'
import { usePrincipalOrgId, useSetPrincipleOrganization } from '~/store/slices/currentOrg'
import { useClearTenantInfo } from '~/store/slices/tenant'

if (config.datadog) datadogRum.init(config.datadog)
// Inside the desktop shell, mark this session as desktop (no-op in a browser).
void tagDesktopTelemetry()

export type UserDataType = {
  email: string
  id: string
  name: string
  role?: string
  sites?: string[]
  organizationId?: string
  /** Access level: read-only users keep their role's visibility but lose all write actions. */
  readOnly?: boolean
}

type AuthValuesType = {
  loading: boolean
  logout: () => void
  user: UserDataType | null
}

// ** Defaults
const defaultProvider: AuthValuesType = {
  user: null,
  loading: true,
  logout: () => null
}

const AuthContext = createContext(defaultProvider)

const LOADING_PLACEHOLDER = 'loading'

type AuthUser = { userId: string }
type Props = PropsWithChildren<{ user: AuthUser }>

export const AuthProvider = ({ user: authUser, children }: Props) => {
  const { logout, getAccessTokenSilently } = useAuth0()
  const { user, dbUser, isLoading } = useConsolidatedUser(authUser)
  useBootMark({ phase: 'current-user-resolved', when: !!dbUser })
  const principalOrgId = usePrincipalOrgId()
  const setPrincipalOrganization = useSetPrincipleOrganization()
  const clearTenantInfo = useClearTenantInfo()

  const handleLogout = useCallback(() => {
    // Stop Datadog session replay recording
    datadogRum.stopSessionReplayRecording()
    datadogLogs.clearUser()

    // Remove the user from localStorage
    window.localStorage.removeItem('userData')

    // Drop per-tab cloak restore so the next login does not reopen a child org.
    clearCloakedOrgPath()

    // Clear tenant info from redux
    clearTenantInfo()

    // Sign out from Auth0
    logout({ logoutParams: { returnTo: window.location.origin } })
  }, [logout, clearTenantInfo])

  // set getAccessTokenSilently in global module to reuse it outside a React component
  setAccessTokenSilently(getAccessTokenSilently)
  setLogout(handleLogout)

  // Set the Principal Org on login
  useEffect(() => {
    if (dbUser?.organizationId !== principalOrgId) {
      setPrincipalOrganization(dbUser?.organizationId)
    }
  }, [dbUser, principalOrgId, setPrincipalOrganization])

  // By the time this code is executed, we have already authenticated with Auth0. However, we need to wait for
  // the `useUser` hook to finish before we can set the user in the context. While we are waiting for that request
  // to complete, we need to set a flag in localStorage to indicate that the user is logged in, so that the
  // `AuthGuard` doesn't redirect the user to the index page.
  useFirstRender(() => {
    const userData = window.localStorage.getItem('userData')
    window.localStorage.setItem('userData', userData ?? LOADING_PLACEHOLDER)
  })

  const values = useMemo<AuthValuesType>(
    () => ({
      user,
      loading: isLoading,
      logout: handleLogout
    }),
    [user, handleLogout, isLoading]
  )

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext }

/**
 * Consolidates the Auth0 user and the user data from the database into a single object, and
 * stores it in localStorage.
 *
 * This also initiates user session recording with Datadog.
 */
function useConsolidatedUser(authUser?: AuthUser) {
  const logger = useLogger('AuthProvider')
  const storedUser = useStoredUser()
  const { user: dbUser, isLoading } = useUser(authUser?.userId)

  const sessionStarted = useRef(false)
  const [user, setUser] = useState<UserDataType | null>(storedUser)
  const router = useRouter()
  const searchParams = useSearchParams()

  // Begin Datadog session recording when the user is logged in
  useEffect(() => {
    if (authUser && user && !sessionStarted.current) {
      sessionStarted.current = true
      logger.info('Starting Datadog session recording')
      const datadogUser = {
        id: user.id,
        email: user.email,
        name: user.name,
        organizationId: user.organizationId
      }
      datadogRum.setUser(datadogUser)
      datadogLogs.setUser(datadogUser)
      datadogRum.startSessionReplayRecording()
    }
  }, [authUser, logger, user])

  // When the user is logged in and we have their data from the database, store the data in localStorage.
  useEffect(() => {
    if (authUser && dbUser) {
      const userData: UserDataType = {
        email: dbUser.email!,
        id: authUser.userId,
        name: dbUser.name ?? '',
        role: dbUser.roles?.at(0)?.toLowerCase(),
        sites: dbUser.sites,
        organizationId: dbUser.organizationId,
        readOnly: dbUser.readOnly
      }

      // If no user object was present in local storage, then the user has just logged in. We need to
      // clear the timezone snooze state.
      if (!user) {
        clearTimezoneSnooze(userData.id)
      }

      // Persist preferred language so the next boot can start fetching
      // /locales/{lang}.json in parallel with /me, instead of waiting on it.
      writeCachedPreferredLanguage(dbUser.preferredLanguage)

      if (isEqual(user, userData)) return

      setUser(userData)
      window.localStorage.setItem('userData', JSON.stringify(userData))

      // If there is a `returnUrl` send the user to it, otherwise send them to the root
      const returnUrl = searchParams?.get('returnUrl')
      if (returnUrl) {
        router.replace(returnUrl)
      }
    }
    // Do not re-run the effect when the search params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, dbUser, router, user])

  return { user, dbUser, isLoading }
}

/**
 * Checks if there is user data stored in the localStorage `userData` field, verifies that it is valid, and returns
 * it if so. Otherwise, returns null.
 */
function useStoredUser(): UserDataType | null {
  const storedUserStr = window.localStorage.getItem('userData')
  return useMemo(() => {
    if (!storedUserStr) return null

    let storedUser: unknown
    try {
      storedUser = JSON.parse(storedUserStr)
    } catch (e) {
      return null
    }

    if (typeof storedUser !== 'object' || storedUser === null) return null

    // Verify that the saved object is valid
    const fieldsToCheck: Array<keyof UserDataType> = ['email', 'name', 'id', 'role']
    const hasAllFields = fieldsToCheck.every((field) => {
      return (
        Object.hasOwnProperty.call(storedUser, field) &&
        typeof (storedUser as Record<string, unknown>)[field] === 'string'
      )
    })

    if (hasAllFields) {
      return storedUser as UserDataType
    } else {
      return null
    }
  }, [storedUserStr])
}

/**
 * Returns the user data from localStorage parsed into a `UserDataType` object. This makes user data
 * available to components high in the component tree, outside the `AuthProvider` or `ReduxProvider`.
 * In particular, the `Rollbar` component needs this information to send identifying information in
 * error reports.
 */
export function getUserData() {
  const data = window.localStorage.getItem('userData')
  if (!data || data === LOADING_PLACEHOLDER) return null

  try {
    return JSON.parse(data) as UserDataType
  } catch (e) {
    return null
  }
}
